<template>
  <div class="premium-page">
    <!-- ---------------------- HERO ---------------------- -->
    <section class="premium-hero">
      <div class="container premium-hero__inner">
        <div class="premium-hero__text">
          <span class="premium-hero__pill">
            <i class="fas fa-crown"></i> Carl-bot Premium
          </span>
          <h1 class="premium-hero__title">
            Get more out of every Discord server you run.
          </h1>
          <p class="premium-hero__subtitle">
            Higher limits, exclusive tools and quality-of-life upgrades for
            communities that have outgrown the free tier.
          </p>
          <div class="premium-hero__cta">
            <PatreonPremiumButton @track="trackPremiumCTA" />
            <DiscordPremiumButton @track="trackPremiumCTA" />
          </div>
        </div>
        <div class="premium-hero__visual d-none d-lg-block">
          <b-img src="/img/turtle-crown.png" alt="Carl-bot Premium" class="premium-hero__mascot" />
        </div>
      </div>
    </section>

    <!-- ---------------------- COMPARE ---------------------- -->
    <section class="premium-compare container">
      <h2 class="premium-section-title">Free vs Premium</h2>
      <p class="premium-section-lede">
        What changes the moment you upgrade.
      </p>
      <div class="compare-table">
        <div class="compare-row compare-row--head">
          <div class="compare-cell compare-cell--feature">Feature</div>
          <div class="compare-cell compare-cell--free">Free</div>
          <div class="compare-cell compare-cell--premium">
            <i class="fas fa-crown"></i> Premium
          </div>
        </div>
        <div class="compare-row" v-for="row in compareRows" :key="row.feature">
          <div class="compare-cell compare-cell--feature">{{ row.feature }}</div>
          <div class="compare-cell compare-cell--free">{{ row.free }}</div>
          <div class="compare-cell compare-cell--premium">{{ row.premium }}</div>
        </div>
      </div>
    </section>

    <!-- ---------------------- HEADLINE FEATURES ---------------------- -->
    <section class="premium-features container">
      <h2 class="premium-section-title">Everything Premium unlocks</h2>
      <p class="premium-section-lede">
        The full toolkit, hand-picked for serious community owners.
      </p>
      <div class="features-grid features-grid--headline">
        <div
          v-for="f in headlineFeatures"
          :key="f.title"
          class="feature-tile feature-tile--headline"
        >
          <div class="feature-tile__icon" :style="{ background: f.color }">
            <i :class="f.icon"></i>
          </div>
          <h3 class="feature-tile__title">{{ f.title }}</h3>
          <p class="feature-tile__desc">{{ f.description }}</p>
        </div>
      </div>

      <div class="features-grid features-grid--rest">
        <div
          v-for="f in restFeatures"
          :key="f.title"
          class="feature-tile feature-tile--mini"
        >
          <div class="feature-tile__icon-mini">
            <i :class="f.icon || 'fas fa-check'"></i>
          </div>
          <div>
            <h4 class="feature-tile__title-mini">{{ f.title }}</h4>
            <p class="feature-tile__desc-mini">{{ f.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ---------------------- FAQ ---------------------- -->
    <section class="premium-faq container">
      <h2 class="premium-section-title">Common questions</h2>
      <div class="faq-grid">
        <div class="faq-item" v-for="(item, i) in faq" :key="i">
          <h4 class="faq-item__q">{{ item.q }}</h4>
          <p class="faq-item__a">{{ item.a }}</p>
        </div>
      </div>
    </section>

    <!-- ---------------------- FINAL CTA ---------------------- -->
    <section class="premium-final container">
      <div class="premium-final__inner">
        <h2 class="premium-final__title">Ready to upgrade?</h2>
        <p class="premium-final__text">
          Join the communities already running on Carl-bot Premium.
        </p>
        <div class="premium-final__cta">
          <PatreonPremiumButton @track="trackPremiumCTA" />
          <DiscordPremiumButton @track="trackPremiumCTA" />
        </div>
        <p class="premium-final__support">
          Questions before you buy?
          <a href="https://discord.gg/S2ZkBTnd8X" target="_blank">
            Ask in our support server →
          </a>
        </p>
      </div>
    </section>

    <!-- ---------------------- FOOTER ---------------------- -->
    <footer class="footer">
      <b-container>
        <b-row>
          <b-col>
            <h6 class="tinytitle">Carl.gg</h6>A service provided by
            <a href="https://botlabs.gg">BotLabs.gg</a> | All Rights Reserved
          </b-col>
          <b-col>
            <div class="float-right">
              <b-link to="/terms-of-use"><b class="footer_title">Terms of Use</b></b-link><br />
              <a href="https://botlabs.gg/privacy-policy"><b class="footer_title">Privacy Policy</b></a><br />
              <a href="https://botlabs.gg/copyright-dispute-policy"><b class="footer_title">Copyright Dispute Policy</b></a><br />
              <a href="https://botlabs.gg/gdpr-privacy-notice"><b class="footer_title">GDPR Privacy Notice</b></a><br />
              <a href="https://botlabs.gg/cookie-policy"><b class="footer_title">Cookie Policy</b></a><br />
              <a href="https://botlabs.gg/end-user-license-agreement"><b class="footer_title">End User License Agreement</b></a><br />
            </div>
          </b-col>
        </b-row>
      </b-container>
    </footer>
  </div>
</template>

<script>
import features from "./premium-features.json";
import DiscordPremiumButton from "../../components/DiscordPremiumButton.vue";
import PatreonPremiumButton from "../../components/PatreonPremiumButton.vue";

export default {
  name: "PremiumShowcase",
  components: { DiscordPremiumButton, PatreonPremiumButton },
  data() {
    return {
      features,
      compareRows: [
        { feature: "Reaction roles per server", free: "250", premium: "1,000" },
        { feature: "YouTube channel alerts", free: "5", premium: "20" },
        { feature: "Twitch stream alerts", free: "2", premium: "5" },
        { feature: "Weblog entries", free: "100", premium: "500" },
        { feature: "Levels & XP system", free: "No", premium: "Yes" },
        { feature: "Sticky messages", free: "No", premium: "Yes" },
        { feature: "Auto Purge", free: "No", premium: "Yes" },
        { feature: "Voice-role links", free: "No", premium: "Yes" },
        { feature: "Timed reaction roles", free: "No", premium: "Yes" },
        { feature: "Separate farewell channel", free: "No", premium: "Yes" },
        { feature: "Drama Watcher (mod actions)", free: "No", premium: "Yes" },
        { feature: "Custom bot avatar & banner", free: "No", premium: "Yes" },
      ],
      faq: [
        {
          q: "Running multiple servers? Can I use Premium on more than one?",
          a: "Yes, with Patreon. Higher Patreon tiers come with extra slots — each slot covers one server, and you can move slots between servers whenever you want. Discord subscriptions cover a single server per subscription.",
        },
        {
          q: "Patreon or Discord, which should I pick?",
          a: "Both unlock the same per-server features. If you only need Premium on one server, either works. If you run multiple servers, Patreon is the one that lets a single subscription cover several at once.",
        },
        {
          q: "Can I cancel anytime?",
          a: "Yes. You can cancel from Patreon or Discord at any time and keep your Premium features until the end of the billing period.",
        },
        {
          q: "What happens if Premium expires?",
          a: "Your premium-only data stays safe. Premium-only features stop working, and limits drop back to the free tier, but you can resubscribe at any time and pick up where you left off.",
        },
        {
          q: "Need help deciding?",
          a: "Hop into our support server and ask. We're happy to walk you through what makes sense for your community.",
        },
      ],
    };
  },
  computed: {
    headlineFeatures() {
      return this.features.filter((f) => f.headline);
    },
    restFeatures() {
      return this.features.filter((f) => !f.headline);
    },
  },
  methods: {
    trackPremiumCTA(source) {
      if (this.$ga) {
        this.$ga.event("Premium", "premium_cta_click", source);
      }
    },
  },
};
</script>

<style scoped>
.premium-page {
  color: #fefefe;
  font-family: "Poppins", "Roboto", sans-serif;
}

/* ---------------------- HERO ---------------------- */
.premium-hero {
  padding: 60px 0 50px;
}
.premium-hero__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
}
.premium-hero__text {
  flex: 1;
  max-width: 620px;
}
.premium-hero__pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 14px;
  background: rgba(248, 203, 0, 0.12);
  border: 1px solid rgba(248, 203, 0, 0.4);
  color: #f8cb00;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: 999px;
  margin-bottom: 18px;
}
.premium-hero__title {
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -1.5px;
  color: #fff;
  margin-bottom: 18px;
}
.premium-hero__subtitle {
  font-size: 19px;
  line-height: 1.55;
  color: #d8d6e0;
  margin-bottom: 28px;
}
.premium-hero__cta {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}
.premium-hero__visual {
  flex-shrink: 0;
}
.premium-hero__mascot {
  width: 380px;
  max-width: 100%;
  filter: drop-shadow(0 12px 28px rgba(248, 203, 0, 0.25));
}

/* ---------------------- SECTION HEADERS ---------------------- */
.premium-section-title {
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: 34px;
  color: #fff;
  text-align: center;
  margin-bottom: 10px;
  letter-spacing: -0.5px;
}
.premium-section-lede {
  font-size: 16px;
  color: #b8b6c8;
  text-align: center;
  max-width: 580px;
  margin: 0 auto 36px;
}

/* ---------------------- COMPARE TABLE ---------------------- */
.premium-compare {
  padding: 40px 15px 30px;
}
.compare-table {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  overflow: hidden;
  max-width: 820px;
  margin: 0 auto;
}
.compare-row {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr;
  align-items: center;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}
.compare-row:first-child {
  border-top: none;
}
.compare-row--head {
  background: rgba(255, 255, 255, 0.04);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 700;
  color: #b8b6c8;
}
.compare-row--head .compare-cell {
  padding: 14px 18px;
}
.compare-cell {
  padding: 14px 18px;
  font-size: 14.5px;
  color: #d8d6e0;
}
.compare-cell--feature {
  color: #fff;
  font-weight: 500;
}
.compare-cell--free {
  color: #999;
  text-align: center;
}
.compare-cell--premium {
  color: #f8cb00;
  font-weight: 600;
  text-align: center;
  background: rgba(248, 203, 0, 0.04);
}
.compare-row--head .compare-cell--premium {
  color: #f8cb00;
}
.compare-cell--premium i {
  font-size: 11px;
  margin-right: 4px;
}

@media (max-width: 575.98px) {
  .compare-row {
    grid-template-columns: 1.2fr 0.8fr 0.9fr;
  }
  .compare-cell {
    padding: 12px 10px;
    font-size: 13px;
  }
}

/* ---------------------- FEATURE GRIDS ---------------------- */
.premium-features {
  padding: 50px 15px 40px;
}
.features-grid {
  display: grid;
  gap: 18px;
}
.features-grid--headline {
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  margin-bottom: 28px;
}
.features-grid--rest {
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}
.feature-tile {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 22px;
  transition: transform 0.2s ease, border-color 0.2s ease;
}
.feature-tile:hover {
  transform: translateY(-3px);
  border-color: rgba(158, 128, 255, 0.35);
}
.feature-tile--headline .feature-tile__icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #fff;
  margin-bottom: 16px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.3);
}
.feature-tile__title {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: #fff;
  margin-bottom: 8px;
}
.feature-tile__desc {
  font-size: 14px;
  line-height: 1.55;
  color: #b8b6c8;
  margin: 0;
}

.feature-tile--mini {
  display: flex;
  gap: 14px;
  padding: 16px 18px;
}
.feature-tile__icon-mini {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: rgba(158, 128, 255, 0.15);
  color: #9e80ff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}
.feature-tile__title-mini {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 15px;
  color: #fff;
  margin-bottom: 3px;
}
.feature-tile__desc-mini {
  font-size: 13px;
  line-height: 1.45;
  color: #b8b6c8;
  margin: 0;
}

/* ---------------------- FAQ ---------------------- */
.premium-faq {
  padding: 50px 15px 40px;
}
.faq-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
  max-width: 900px;
  margin: 0 auto;
}
.faq-item {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 12px;
  padding: 20px 22px;
}
.faq-item__q {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 15px;
  color: #fff;
  margin: 0 0 8px;
}
.faq-item__a {
  font-size: 13.5px;
  line-height: 1.55;
  color: #b8b6c8;
  margin: 0;
}

/* ---------------------- FINAL CTA ---------------------- */
.premium-final {
  padding: 30px 15px 60px;
}
.premium-final__inner {
  background: linear-gradient(135deg, rgba(248, 203, 0, 0.1), rgba(255, 152, 0, 0.06));
  border: 1px solid rgba(248, 203, 0, 0.3);
  border-radius: 20px;
  padding: 50px 30px;
  text-align: center;
}
.premium-final__title {
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: 32px;
  color: #fff;
  margin-bottom: 10px;
}
.premium-final__text {
  font-size: 16px;
  color: #d8d6e0;
  margin-bottom: 26px;
}
.premium-final__cta {
  display: flex;
  gap: 14px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 22px;
}
.premium-final__support {
  font-size: 13px;
  color: #b8b6c8;
  margin: 0;
}
.premium-final__support a {
  color: #f8cb00;
  font-weight: 600;
  text-decoration: none;
}
.premium-final__support a:hover {
  color: #ffd83d;
  text-decoration: none;
}

/* ---------------------- FOOTER ---------------------- */
.footer {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding: 30px 0 24px;
  margin-top: 20px;
}
.footer a,
.footer .footer_title,
.footer b {
  color: #c8c6d6 !important;
  text-decoration: none;
  font-size: 13.5px;
}
.footer a:hover,
.footer .footer_title:hover {
  color: #9e80ff !important;
}

/* ---------------------- RESPONSIVE ---------------------- */
@media (max-width: 991.98px) {
  .premium-hero__inner {
    text-align: center;
  }
  .premium-hero__text {
    margin: 0 auto;
  }
  .premium-hero__cta {
    justify-content: center;
  }
  .premium-hero__title {
    font-size: 40px;
  }
  .premium-section-title {
    font-size: 28px;
  }
}
@media (max-width: 575.98px) {
  .premium-hero {
    padding: 36px 0 30px;
  }
  .premium-hero__title {
    font-size: 32px;
  }
  .premium-hero__subtitle {
    font-size: 16px;
  }
  .premium-section-title {
    font-size: 26px;
  }
  .premium-final__inner {
    padding: 36px 22px;
  }
  .premium-final__title {
    font-size: 24px;
  }
}
</style>
